/**
 * Remembers where a signer-upper was in the flow so email confirmation can
 * drop them back exactly where they left off instead of the landing page.
 */
const KEY = "reserve.signup.resume";

export type SignupResume = {
  email: string | null;
  role: string | null;
  /** Same-origin path only, e.g. "/application-status" */
  path: string;
  savedAt: number;
};

/** Only same-origin absolute paths are ever accepted as a destination. */
export function safeInternalPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (!/^\/[^/]/.test(value)) return null; // must start with "/" but not "//"
  if (value.includes("\\") || /^\/+\s*javascript:/i.test(value)) return null;
  return value;
}

export function saveSignupResume(input: {
  email?: string | null;
  role?: string | null;
  path: string;
}): void {
  const path = safeInternalPath(input.path);
  if (!path) return;
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        email: input.email ?? null,
        role: input.role ?? null,
        path,
        savedAt: Date.now(),
      } satisfies SignupResume),
    );
  } catch {
    /* storage unavailable — fall back to the URL param */
  }
}

export function readSignupResume(): SignupResume | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SignupResume;
    const path = safeInternalPath(parsed?.path);
    if (!path) return null;
    // Stale after 7 days.
    if (parsed.savedAt && Date.now() - parsed.savedAt > 7 * 24 * 60 * 60_000) return null;
    return { ...parsed, path };
  } catch {
    return null;
  }
}

export function clearSignupResume(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/** Builds the same-origin callback used by every signup confirmation send/resend. */
export function buildEmailConfirmationRedirect(origin: string, next: unknown = "/dashboard"): string {
  const destination = safeInternalPath(next) ?? "/dashboard";
  const url = new URL("/verify-email", origin);
  url.searchParams.set("next", destination);
  return url.toString();
}

/** Where to land after confirmation: ?next= wins, then saved progress, then dashboard. */
export function resolveResumeDestination(searchNext?: unknown): string {
  return safeInternalPath(searchNext) ?? readSignupResume()?.path ?? "/dashboard";
}

/** Human labels for Supabase auth-callback error codes. */
export function describeAuthLinkError(
  code: string | null,
  description: string | null,
): { title: string; body: string; kind: "expired" | "used" | "mismatch" | "generic" } | null {
  if (!code && !description) return null;
  const text = `${code ?? ""} ${description ?? ""}`.toLowerCase();
  if (text.includes("expired") || text.includes("otp_expired")) {
    return {
      kind: "expired",
      title: "That confirmation link has expired",
      body: "Links are valid for a short time. Send yourself a fresh one below and open it straight away.",
    };
  }
  if (text.includes("already") || text.includes("confirmed")) {
    return {
      kind: "used",
      title: "This email is already confirmed",
      body: "The link has already been used. Just sign in and you'll pick up where you left off.",
    };
  }
  if (text.includes("email_address_invalid") || text.includes("not found") || text.includes("user_not_found")) {
    return {
      kind: "mismatch",
      title: "We couldn't match that link to an account",
      body: "The address may be mistyped, or the link belongs to a different account. Check the address below and resend, or sign up again.",
    };
  }
  return {
    kind: "generic",
    title: "That link couldn't be used",
    body: description || "Send yourself a new confirmation email below, then open it on this device.",
  };
}

/** Reads Supabase error params from either the query string or the hash. */
export function readAuthLinkErrorFromUrl(href: string): { code: string | null; description: string | null } {
  try {
    const url = new URL(href);
    const hash = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
    const code =
      url.searchParams.get("error_code") ??
      url.searchParams.get("error") ??
      hash.get("error_code") ??
      hash.get("error");
    const description =
      url.searchParams.get("error_description") ?? hash.get("error_description");
    return { code, description: description ? description.replace(/\+/g, " ") : null };
  } catch {
    return { code: null, description: null };
  }
}
